import { ArrowRight, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

const ServiceTestimonials = ({ testimonials }) => {
    const [current, setCurrent] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    // Clean testimonials
    const cleanTestimonials = testimonials.map(item => {
        let [text, author] = item.split(" -- ");
        if (text && author) {
            return {
                text,
                author
            };
        }
        return { text: item, author: "Patient" };
    });

    const next = () => {
        setCurrent(current === cleanTestimonials.length - 1 ? 0 : current + 1);
    };

    const prev = () => {
        setCurrent(current === 0 ? cleanTestimonials.length - 1 : current - 1);
    };

    return (
        <section
            ref={sectionRef}
            className={`py-16 bg-white transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
            <div className="max-w-6xl mx-auto px-4">
                <h2 className="text-3xl font-bold mb-2 text-gray-700">Patient Testimonials</h2>
                <div className="w-24 h-1 bg-lime-500 mb-12 mt-2"></div>

                <div className="bg-gradient-to-r from-teal-500 to-cyan-600 rounded-2xl shadow-lg p-8 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full">
                        <div className="absolute top-0 right-0 opacity-20">
                            <MessageSquare className="h-64 w-64 text-white" />
                        </div>
                    </div>

                    <div className="relative z-10">
                        <div className="testimonial-carousel relative">
                            {cleanTestimonials.map((testimonial, index) => (
                                <div
                                    key={index}
                                    className={`transition-opacity duration-500 ${current === index ? 'block opacity-100' : 'hidden opacity-0'
                                        }`}
                                >
                                    <div className="py-8 px-4 md:px-8 text-center">
                                        <div className="mb-8 mx-auto bg-lime-400/30 rounded-full p-3 inline-flex">
                                            <MessageSquare className="h-8 w-8 text-white" />
                                        </div>
                                        <p className="text-xl md:text-2xl text-white italic mb-6">"{testimonial.text}"</p>
                                        <p className="text-lg text-white/80 font-medium">— {testimonial.author}</p>
                                    </div>
                                </div>
                            ))}

                            {/* Navigation arrows */}
                            <div className="absolute top-1/2 transform -translate-y-1/2 left-0 md:-left-4">
                                <button
                                    onClick={prev}
                                    className="bg-lime-400/40 hover:bg-lime-400/60 rounded-full p-2 text-white transition-all duration-300"
                                >
                                    <ChevronLeft className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="absolute top-1/2 transform -translate-y-1/2 right-0 md:-right-4">
                                <button
                                    onClick={next}
                                    className="bg-lime-400/40 hover:bg-lime-400/60 rounded-full p-2 text-white transition-all duration-300"
                                >
                                    <ChevronRight className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Pagination dots */}
                            <div className="flex justify-center mt-6 space-x-2">
                                {cleanTestimonials.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrent(index)}
                                        className={`h-3 rounded-full transition-all duration-300 ${current === index ? 'bg-lime-400 w-6' : 'bg-white/50 w-3'
                                            }`}
                                        aria-label={`Go to testimonial ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-12 text-center">
                    <a
                        href="#"
                        className="inline-flex items-center text-teal-600 font-semibold hover:text-lime-600 transition-colors duration-300"
                    >
                        Read More Patient Stories <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                </div>
            </div>
        </section>
    );
};

export default ServiceTestimonials;